import { User, Settings, HelpCircle, ExternalLink, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { WormbleLogo } from "@/components/ui/WormbleLogo";
import { PWAStatusBadge } from "@/components/pwa/PWAStatusBadge";
import { usePWA } from "@/hooks/usePWA";
import { UserMenu } from "@/components/layout/UserMenu";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const formatDateForDisplay = (date: Date): string => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const dayName = days[date.getDay()];
  const monthName = months[date.getMonth()];
  const dayNum = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${dayName} ${monthName} ${dayNum}, ${year}`;
};

interface CompactHeaderProps {
  puzzleNumber: number;
  gameDate: Date;
  isCompleted?: boolean;
  isWon?: boolean;
  onSettingsClick: () => void;
  onArchiveClick: () => void;
  onTitleClick?: () => void;
  onHelpClick: () => void;
  debugEnabled?: boolean;
  isArchivePuzzle?: boolean;
  onInstallClick?: () => void;
  onStatsClick: () => void;
}

export function CompactHeader({ 
  puzzleNumber,
  gameDate,
  isCompleted,
  isWon,
  onSettingsClick,
  onArchiveClick,
  onTitleClick,
  onHelpClick,
  debugEnabled,
  isArchivePuzzle,
  onInstallClick,
  onStatsClick
}: CompactHeaderProps) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { isInstalled } = usePWA();

  return (
    <header className="sticky top-0 z-20 w-full bg-background/60 dark:bg-background/40 backdrop-blur-md border-b border-border/50 shadow-sm">
      <div className="max-w-md mx-auto px-3 py-2 sm:py-2.5 md:landscape:py-1.5 grid grid-cols-3 items-center">
        {/* Left: Puzzle Number + Date */}
        <button
          onClick={onArchiveClick}
          className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-2 py-1 -mx-2 -my-1 rounded-md hover:bg-muted/50"
          aria-label="View puzzle archive"
          data-archive-link
        >
          <span>#{puzzleNumber}</span>
          <span className="text-[10px] sm:text-xs text-muted-foreground" aria-hidden="true">•</span>
          <span>{formatDateForDisplay(gameDate)}</span>
          {isCompleted && (
            <span className="ml-1">
              {isWon ? "✅" : "❌"}
            </span>
          )}
        </button>

        {/* Center: Wormble Logo */}
        <div className="text-center">
          <WormbleLogo 
            linkTo="/" 
            size="sm" 
            onClick={onTitleClick} 
            className="cursor-pointer" 
          />
        </div>

        {/* Right: Action Pills */}
        <div className="flex items-center gap-1.5 justify-end">
          {/* User Icon - Show sign in or UserMenu */}
          {isAuthenticated ? (
            <UserMenu onStatsClick={onStatsClick} />
          ) : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/auth")}
              className="h-11 w-11 sm:h-9 sm:w-9 rounded-full bg-card/40 hover:bg-card/60 backdrop-blur-sm"
              aria-label="Sign in"
              data-sign-in
            >
              <User className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
            </Button>
          )}

          {/* PWA Status Badge */}
          {!isInstalled && <PWAStatusBadge />}

          {/* Help Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-11 w-11 sm:h-9 sm:w-9 rounded-full bg-card/40 hover:bg-card/60 backdrop-blur-sm"
                aria-label="Help and resources"
              >
                <HelpCircle className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-background border-border z-50">
              {!isInstalled && onInstallClick && (
                <>
                  <DropdownMenuItem onClick={onInstallClick} className="cursor-pointer font-medium">
                    <Smartphone className="h-4 w-4 mr-2" />
                    📱 Install App
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={onHelpClick} className="cursor-pointer">
                How to Play
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/help")} className="cursor-pointer">
                <span className="flex-1">Help & FAQ</span>
                <ExternalLink className="h-3 w-3 ml-2 opacity-70" aria-hidden="true" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/blog")} className="cursor-pointer">
                <span className="flex-1">Blog</span>
                <ExternalLink className="h-3 w-3 ml-2 opacity-70" aria-hidden="true" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/about")} className="cursor-pointer">
                <span className="flex-1">About</span>
                <ExternalLink className="h-3 w-3 ml-2 opacity-70" aria-hidden="true" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/contact")} className="cursor-pointer">
                <span className="flex-1">Contact</span>
                <ExternalLink className="h-3 w-3 ml-2 opacity-70" aria-hidden="true" />
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/dev-notes")} className="cursor-pointer">
                <span className="flex-1">Dev Notes</span>
                <ExternalLink className="h-3 w-3 ml-2 opacity-70" aria-hidden="true" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Settings - Circle pill */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onSettingsClick}
            className="h-11 w-11 sm:h-9 sm:w-9 rounded-full bg-card/40 hover:bg-card/60 backdrop-blur-sm relative"
            aria-label="Settings"
          >
            <Settings className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
            {debugEnabled && (
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-orange-500 rounded-full animate-pulse" />
            )}
          </Button>
        </div>
      </div>
    </header>
  );
}
